class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute("disabled") || !("IntersectionObserver" in window)) {
      return;
    }

    this.productList = this.querySelector("product-list");
    this.sentinel = this.querySelector("[data-infinite-scroll-sentinel]");
    this.pagination = this.parentElement?.querySelector(".collection__pagination");
    this.nextLink = this.pagination?.querySelector('[rel="next"][href]');

    if (!this.productList || !this.sentinel || !this.pagination || !this.nextLink) {
      if (!this.nextLink) {
        this.setAttribute("complete", "");
      }

      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      rootMargin: "400px 0px"
    });
    this._observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._abortController?.abort();
  }

  async _onIntersection(entries) {
    if (this._isLoading || !entries.some((entry) => entry.isIntersecting)) {
      return;
    }

    await this._loadNextPage();
  }

  async _loadNextPage() {
    if (!this.nextLink) {
      this._observer?.disconnect();
      this.setAttribute("complete", "");
      return;
    }

    this._isLoading = true;
    this.setAttribute("loading", "");
    this._abortController?.abort();
    this._abortController = new AbortController();

    const nextPageUrl = new URL(this.nextLink.href, window.location.origin);
    nextPageUrl.searchParams.set("section_id", this.getAttribute("section-id"));

    try {
      const response = await fetch(nextPageUrl.toString(), {
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Infinite scroll request failed with status ${response.status}`);
      }

      const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
      const nextSection =
        responseDocument.getElementById(`shopify-section-${this.getAttribute("section-id")}`) ||
        responseDocument.querySelector(".shopify-section");
      const nextProductList = nextSection?.querySelector("collection-infinite-scroll product-list");
      const nextPagination = nextSection?.querySelector(".collection__pagination");

      if (!nextProductList || !nextPagination) {
        throw new Error("Infinite scroll could not find the next product batch.");
      }

      const productFragment = document.createDocumentFragment();

      Array.from(nextProductList.children).forEach((child) => {
        productFragment.appendChild(document.importNode(child, true));
      });

      this.productList.appendChild(productFragment);

      const importedPagination = document.importNode(nextPagination, true);
      this.pagination.replaceWith(importedPagination);
      this.pagination = importedPagination;
      this.nextLink = this.pagination.querySelector('[rel="next"][href]');

      if (this.nextLink) {
        this.removeAttribute("complete");
      } else {
        this._observer?.disconnect();
        this.setAttribute("complete", "");
      }

      if (this.nextLink && this._shouldLoadMore()) {
        requestAnimationFrame(() => this._loadNextPage());
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this._observer?.disconnect();
        console.error(error);
      }
    } finally {
      this._isLoading = false;
      this.removeAttribute("loading");
    }
  }

  _shouldLoadMore() {
    const sentinelBounds = this.sentinel?.getBoundingClientRect();

    return Boolean(sentinelBounds) && sentinelBounds.top <= window.innerHeight + 400;
  }
}

if (!window.customElements.get("collection-infinite-scroll")) {
  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
}
